import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import {
  FaArrowRight,
  FaChartPie,
  FaCreditCard,
  FaGasPump,
  FaMoneyBillWave,
  FaOilCan,
  FaRedoAlt,
  FaWallet,
} from 'react-icons/fa';
import './CSS/Dashboard.css';
import api from '../lib/api';

const paymentColors = ['#16a34a', '#2563eb', '#f59e0b', '#e11d48'];

const moneyFormatter = new Intl.NumberFormat('en-IN', {
  currency: 'INR',
  maximumFractionDigits: 0,
  style: 'currency',
});

const compactMoneyFormatter = new Intl.NumberFormat('en-IN', {
  currency: 'INR',
  notation: 'compact',
  maximumFractionDigits: 1,
  style: 'currency',
});

const quickActions = [
  {
    icon: FaGasPump,
    label: 'Record Readings',
    text: 'Update shift readings and keep live sales clean.',
    to: '/admin/readings',
  },
  {
    icon: FaWallet,
    label: 'Cash Tracking',
    text: 'Trace opening cash, collections, handovers, and closing balance.',
    to: '/admin/cash-tracing',
  },
  {
    icon: FaCreditCard,
    label: 'Track Credits',
    text: 'Check customer and employee dues before closing.',
    to: '/admin/customer-credit',
  },
];

const dashboardRanges = [
  { key: '1d', label: '1 Day', days: 1 },
  { key: '7d', label: '1 Week', days: 7 },
  { key: '14d', label: '2 Weeks', days: 14 },
  { key: '30d', label: '30 Days', days: 30 },
];

function numberVal(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function formatMoney(value, compact = false) {
  return compact ? compactMoneyFormatter.format(value || 0) : moneyFormatter.format(value || 0);
}

export default function Dashboard() {
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRange, setSelectedRange] = useState('30d');
  const [stats, setStats] = useState({
    credit: 0,
    fuelSales: 0,
    lubeSales: 0,
    totalSales: 0,
  });
  const [paymentSplit, setPaymentSplit] = useState([]);

  const paymentTotal = useMemo(
    () => paymentSplit.reduce((sum, item) => sum + numberVal(item.value), 0),
    [paymentSplit]
  );

  const activeRange = useMemo(
    () => dashboardRanges.find((range) => range.key === selectedRange) || dashboardRanges[3],
    [selectedRange]
  );

  const statCards = useMemo(
    () => [
      {
        icon: FaMoneyBillWave,
        label: 'Total Sales',
        tone: 'green',
        value: stats.totalSales,
        helper: `Net revenue for ${activeRange.label.toLowerCase()}`,
      },
      {
        icon: FaGasPump,
        label: 'Fuel Sales',
        tone: 'blue',
        value: stats.fuelSales,
        helper: `Fuel revenue for ${activeRange.label.toLowerCase()}`,
      },
      {
        icon: FaOilCan,
        label: 'Lube Sales',
        tone: 'amber',
        value: stats.lubeSales,
        helper: `Lube revenue for ${activeRange.label.toLowerCase()}`,
      },
      {
        icon: FaCreditCard,
        label: 'Credit',
        tone: 'red',
        value: stats.credit,
        helper: `Credit captured for ${activeRange.label.toLowerCase()}`,
      },
    ],
    [activeRange.label, stats]
  );

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - (activeRange.days - 1));

      const today = end.toISOString().slice(0, 10);
      const startDate = start.toISOString().slice(0, 10);

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const [pnlRes, reconRes] = await Promise.all([
        api.get('/api/reports/pnl', {
          headers,
          params: { endDate: today, startDate },
        }),
        api.get('/api/reconciliation', {
          headers,
          params: { endDate: today, startDate },
        }),
      ]);

      const pnl = pnlRes.data?.pnl || {};
      const recon = reconRes.data || [];

      let cash = 0;
      let upi = 0;
      let card = 0;
      let credit = 0;

      recon.forEach((row) => {
        cash += numberVal(row.cash_amount);
        upi += numberVal(row.upi_amount);
        card += numberVal(row.card_amount);
        credit += numberVal(row.credit_amount);
      });

      setStats({
        credit,
        fuelSales: numberVal(pnl.gross_fuel_revenue),
        lubeSales: numberVal(pnl.gross_lube_revenue),
        totalSales: numberVal(pnl.net_total_revenue || pnl.gross_total_revenue),
      });

      setPaymentSplit([
        { name: 'Cash', value: cash },
        { name: 'UPI', value: upi },
        { name: 'Card', value: card },
        { name: 'Credit', value: credit },
      ]);
    } catch (err) {
      console.error('Dashboard error:', err);
      setError(err?.response?.data?.error || 'Failed to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeRange.days, token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return (
    <div className="ff-admin-dashboard">
      <section className="ff-dash-hero">
        <div>
          <div className="ff-dash-eyebrow">{activeRange.label} View</div>
          <h2>Station performance at a glance</h2>
          <p>
            A clean command view for revenue, payment mix, credits, and the next actions that
            keep the station moving.
          </p>
        </div>
        <div className="ff-dash-tools">
          <div className="ff-range-control" aria-label="Dashboard period">
            {dashboardRanges.map((range) => (
              <button
                className={selectedRange === range.key ? 'active' : ''}
                disabled={loading && selectedRange === range.key}
                key={range.key}
                onClick={() => setSelectedRange(range.key)}
                type="button"
              >
                {range.label}
              </button>
            ))}
          </div>

          <button className="ff-refresh-btn" type="button" onClick={fetchDashboard} disabled={loading}>
            <FaRedoAlt aria-hidden="true" />
            {loading ? 'Refreshing' : 'Refresh'}
          </button>
        </div>
      </section>

      {error && (
        <div className="ff-dashboard-alert" role="alert">
          <strong>Could not refresh dashboard</strong>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <DashboardSkeleton />
      ) : (
        <>
          <section className="ff-stat-grid">
            {statCards.map(({ helper, icon: Icon, label, tone, value }) => (
              <article className={`ff-stat-card ${tone}`} key={label}>
                <div className="ff-stat-icon">
                  <Icon aria-hidden="true" />
                </div>
                <div className="ff-stat-copy">
                  <span>{label}</span>
                  <strong>{formatMoney(value, true)}</strong>
                  <em>{helper}</em>
                </div>
              </article>
            ))}
          </section>

          <section className="ff-dashboard-grid">
            <article className="ff-dashboard-card ff-payment-card">
              <div className="ff-card-head">
                <div>
                  <span>Payment Mix</span>
                  <h3>Distribution by tender type</h3>
                </div>
                <FaChartPie aria-hidden="true" />
              </div>

              <div className="ff-payment-layout">
                <div className="ff-chart-shell">
                  {paymentTotal > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={paymentSplit}
                          dataKey="value"
                          innerRadius={72}
                          outerRadius={108}
                          paddingAngle={3}
                          strokeWidth={0}
                        >
                          {paymentSplit.map((item, index) => (
                            <Cell key={item.name} fill={paymentColors[index]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatMoney(value)} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="ff-empty-chart">No payment data found</div>
                  )}
                </div>

                <div className="ff-payment-list">
                  {paymentSplit.map((item, index) => {
                    const percent = paymentTotal ? Math.round((item.value / paymentTotal) * 100) : 0;
                    return (
                      <div className="ff-payment-row" key={item.name}>
                        <span style={{ '--row-color': paymentColors[index] }} />
                        <div>
                          <strong>{item.name}</strong>
                          <em>{percent}% of payments</em>
                        </div>
                        <b>{formatMoney(item.value, true)}</b>
                      </div>
                    );
                  })}
                </div>
              </div>
            </article>

            <article className="ff-dashboard-card ff-actions-card">
              <div className="ff-card-head">
                <div>
                  <span>Next Best Actions</span>
                  <h3>Fast paths for today</h3>
                </div>
              </div>

              <div className="ff-action-list">
                {quickActions.map(({ icon: Icon, label, text, to }) => (
                  <Link to={to} className="ff-action-row" key={to}>
                    <span className="ff-action-icon">
                      <Icon aria-hidden="true" />
                    </span>
                    <span>
                      <strong>{label}</strong>
                      <em>{text}</em>
                    </span>
                    <FaArrowRight aria-hidden="true" />
                  </Link>
                ))}
              </div>
            </article>
          </section>
        </>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="ff-dashboard-loading" aria-label="Loading dashboard">
      <div />
      <div />
      <div />
      <div />
      <div className="wide" />
    </div>
  );
}
